// src/modules/time-tracking/StandingJobsList.js (admin) — manage standing jobs.
//
// A standing job is a permanent internal clock-in target (the shop, the yard,
// general work). It always appears in the crew's job picker in the mobile app,
// every day, so they can clock in on days with no customer job scheduled. It is
// NOT customer work: it has no estimate or lead and never shows up in the Jobs
// dashboard or the job KPIs. The GPS geofence still applies.
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaEdit, FaTrash, FaMapMarkerAlt, FaInbox, FaToggleOn, FaToggleOff, FaClock, FaRulerHorizontal,
} from 'react-icons/fa';
import DashboardLayout from '../../layouts/DashboardLayout';
import Modal from '../../components/shared/Modal';
import Pagination from '../../components/shared/Pagination';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import FeedbackModal from '../../components/shared/ModalNotification';
import { StatusBadge, ResponsiveHeader } from '../../components/styled/CommonStyles';
import {
  GridContainer, Card, CardHeader, LeadName, CardContent, InfoRow,
  ActionIconsContainer, ActionButton, EmptyState,
} from '../../components/styled/GridComponents';
import { listStandingJobs, deleteStandingJob, updateStandingJob } from './timeTrackingService';

const StandingJobsList = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);

  useEffect(() => { fetchJobs(); }, []);

  const fetchJobs = async () => {
    setLoading(true); setError(null);
    try {
      const { standingJobs } = await listStandingJobs();
      setJobs(standingJobs || []);
    } catch (e) {
      setError('Failed to load standing jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // The day-to-day control: switch the entry in or out of the crew's job list
  // without deleting it, so every hour ever clocked against it is preserved.
  // Optimistic, reverted if the API rejects.
  const [togglingId, setTogglingId] = useState(null);
  const handleToggleActive = async (job) => {
    const next = !job.is_active;
    setTogglingId(job.job_id);
    setJobs((list) => list.map((j) => (j.job_id === job.job_id ? { ...j, is_active: next } : j)));
    try {
      await updateStandingJob(job.job_id, { is_active: next });
    } catch (e) {
      setJobs((list) => list.map((j) => (j.job_id === job.job_id ? { ...j, is_active: job.is_active } : j)));
      setFeedbackMessage('Failed to update standing job: ' + (e.response?.data?.message || e.message));
      setFeedbackSuccess(false); setShowFeedback(true);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteStandingJob(toDelete.job_id);
      setJobs((list) => list.filter((j) => j.job_id !== toDelete.job_id));
      setFeedbackMessage('Standing job deleted successfully');
      setFeedbackSuccess(true);
    } catch (e) {
      // A 409 here means hours were clocked against it — the API refuses rather
      // than silently stripping the job from those shift records.
      setFeedbackMessage('Failed to delete standing job: ' + (e.response?.data?.message || e.message));
      setFeedbackSuccess(false);
    } finally {
      setShowFeedback(true); setLoading(false);
      setDeleteModalOpen(false); setToDelete(null);
    }
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return jobs.filter((j) => !q || j.name?.toLowerCase().includes(q) || j.address?.toLowerCase().includes(q));
  }, [jobs, searchQuery]);

  const current = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <DashboardLayout>
      <ResponsiveHeader
        title="Standing Jobs"
        onSearch={setSearchQuery}
        onAdd={() => navigate('/time-tracking/standing-jobs/new')}
      />

      {loading && <LoadingSpinner />}
      {error && !loading && <EmptyState><p>{error}</p></EmptyState>}

      {!loading && !error && (
        <GridContainer>
          {current.length === 0 ? (
            <EmptyState>
              <FaInbox size={48} />
              <p>
                No standing jobs yet. Add one &mdash; &ldquo;Shop / General Work&rdquo;, say &mdash; and it
                will appear in the crew&rsquo;s clock-in list every single day, so they can clock in on days
                with no customer job scheduled. They still have to be inside the geofence.
              </p>
            </EmptyState>
          ) : current.map((j) => (
            <Card key={j.job_id}>
              <CardHeader>
                <LeadName>{j.name}</LeadName>
                <StatusBadge color={j.is_active ? '#84c959' : '#7f8c8d'}>
                  {j.is_active ? 'In the clock-in list' : 'Switched off'}
                </StatusBadge>
              </CardHeader>
              <CardContent>
                <InfoRow>
                  <FaMapMarkerAlt />
                  <span>
                    {[j.address, j.city, j.state].filter(Boolean).join(', ')
                      || `${j.latitude}, ${j.longitude}`}
                  </span>
                </InfoRow>
                <InfoRow><FaRulerHorizontal /><span>600 ft radius</span></InfoRow>
                <InfoRow>
                  <FaClock />
                  <span>
                    {j.is_active
                      ? 'Available to the crew every day, with or without a scheduled job.'
                      : 'Hidden from the clock-in list. Recorded hours are kept.'}
                  </span>
                </InfoRow>
              </CardContent>
              <ActionIconsContainer>
                <ActionButton
                  onClick={() => handleToggleActive(j)}
                  disabled={togglingId === j.job_id}
                  title={j.is_active
                    ? 'Switch off — hides it from the crew’s clock-in list'
                    : 'Switch on — the crew can clock into it again'}
                >
                  {j.is_active ? <FaToggleOn /> : <FaToggleOff />}
                </ActionButton>
                <ActionButton
                  onClick={() => navigate(`/time-tracking/standing-jobs/edit/${j.job_id}`)}
                  title="Edit standing job"
                >
                  <FaEdit />
                </ActionButton>
                <ActionButton
                  onClick={() => { setToDelete(j); setDeleteModalOpen(true); }}
                  title="Delete standing job"
                >
                  <FaTrash />
                </ActionButton>
              </ActionIconsContainer>
            </Card>
          ))}
        </GridContainer>
      )}

      {!loading && filtered.length > itemsPerPage && (
        <Pagination itemsPerPage={itemsPerPage} totalItems={filtered.length}
          paginate={setCurrentPage} currentPage={currentPage} />
      )}

      {deleteModalOpen && (
        <Modal title="Delete Standing Job"
          message={`Delete "${toDelete?.name || ''}"? The crew will no longer be able to clock into it. If any hours were already clocked against it this will be refused — use the on/off switch instead, which takes it out of the clock-in list and keeps the history intact.`}
          onConfirm={handleDelete}
          onCancel={() => { setDeleteModalOpen(false); setToDelete(null); }}
          isOpen={deleteModalOpen} />
      )}

      <FeedbackModal isOpen={showFeedback} message={feedbackMessage} success={feedbackSuccess}
        onComplete={() => setShowFeedback(false)} />
    </DashboardLayout>
  );
};

export default StandingJobsList;
